export class HttpError extends Error {
  constructor(public status: number, message: string, public payload?: unknown) {
    super(message);
  }
}

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
  });
  if (!res.ok) {
    let payload: unknown;
    try { payload = await res.json(); } catch { /* ignore */ }
    throw new HttpError(res.status, `${res.status} ${res.statusText}`, payload);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export interface AuthUser {
  id: number;
  email: string;
  name: string | null;
  role: 'admin' | 'viewer';
}

export interface UserRow extends AuthUser {
  active: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

export interface OzonSettings {
  clientId: string;
  apiKeyMasked: string;
  hasApiKey: boolean;
  premium: boolean;
  verifiedAt: string | null;
  lastError: string | null;
  updatedAt: string | null;
}

export interface ProductRow {
  productId: string;
  offerId: string;
  sku: string | null;
  name: string;
  brand: string | null;
  category: string | null;
  image: string | null;
  visible: boolean;
  archived: boolean;
  price: string | null;
  oldPrice: string | null;
  minPrice: string | null;
  marketingPrice: string | null;
  colorIndex: 'WITHOUT_INDEX' | 'GREEN' | 'YELLOW' | 'RED' | null;
  extPriceIndex: string | null;
  extMinPrice: string | null;
  ozonPriceIndex: string | null;
  ozonMinPrice: string | null;
  mpPriceIndex: string | null;
  mpMinPrice: string | null;
  rrpPrice: string | null;
  rrpUpdatedAt: string | null;
  rrpViolation: { effective: number; rrp: number; gap: number; gapPct: number } | null;
  stockTotal: number;
  updatedAt: string;
}

export const api = {
  // ---- auth ----
  login: (email: string, password: string) =>
    http<{ user: AuthUser }>('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  logout: () => http<void>('/api/auth/logout', { method: 'POST' }),
  me: () => http<{ user: AuthUser }>('/api/auth/me'),

  // ---- users (admin) ----
  users: () => http<{ items: UserRow[] }>('/api/users'),
  userCreate: (body: { email: string; password: string; name?: string; role: 'admin' | 'viewer' }) =>
    http<UserRow>('/api/users', { method: 'POST', body: JSON.stringify(body) }),
  userUpdate: (id: number, body: Partial<{ name: string; role: 'admin' | 'viewer'; active: boolean; password: string }>) =>
    http<UserRow>(`/api/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  userDelete: (id: number) => http<void>(`/api/users/${id}`, { method: 'DELETE' }),

  // ---- settings ----
  ozonSettings: () => http<OzonSettings>('/api/settings/ozon'),
  ozonSettingsSave: (body: { clientId: string; apiKey?: string; premium: boolean }) =>
    http<OzonSettings>('/api/settings/ozon', { method: 'PUT', body: JSON.stringify(body) }),
  ozonSettingsTest: () => http<{ ok: boolean; totalProducts?: number; error?: string }>('/api/settings/ozon/test', { method: 'POST' }),

  // ---- existing ----
  health: () => http<{ status: string; db: string; ozon: { configured: boolean; premium: boolean; source: 'db' | 'env' | null } }>('/health/full'),
  products: (params: { q?: string; limit?: number; offset?: number } = {}) => {
    const sp = new URLSearchParams();
    if (params.q) sp.set('q', params.q);
    if (params.limit) sp.set('limit', String(params.limit));
    if (params.offset) sp.set('offset', String(params.offset));
    return http<{ total: number; items: ProductRow[] }>(`/api/products?${sp.toString()}`);
  },
  // ---- strategies ----
  strategies: () => http<{ items: { id: number; name: string; enabled: boolean; scheduleCron: string; createdAt: string; _count: { products: number; jobs: number } }[] }>('/api/strategies'),
  strategyGet: (id: number) => http<StrategyDetail>(`/api/strategies/${id}`),
  strategyCreate: (body: StrategyCreatePayload) =>
    http<{ id: number }>('/api/strategies', { method: 'POST', body: JSON.stringify(body) }),
  strategyUpdate: (id: number, body: Partial<StrategyCreatePayload>) =>
    http<unknown>(`/api/strategies/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  strategyDelete: (id: number) => http<void>(`/api/strategies/${id}`, { method: 'DELETE' }),
  strategySetProducts: (id: number, products: { productId: string; minPrice?: number; maxPrice?: number }[]) =>
    http<{ ok: boolean; count: number }>(`/api/strategies/${id}/products`, { method: 'POST', body: JSON.stringify({ products }) }),
  strategyJobs: (id: number) => http<{ items: PriceJob[] }>(`/api/strategies/${id}/jobs`),
  strategyRun: (id: number, dryRun = false) =>
    http<{ accepted: boolean; jobId: string; strategyId: number; dryRun: boolean }>(`/api/strategies/${id}/run`, { method: 'POST', body: JSON.stringify({ dryRun }) }),
  strategyPreview: (id: number) =>
    http<{ jobId: string; productsTried: number; productsUpdated: number; productsSkipped: number; decisions: PreviewDecision[] }>(`/api/strategies/${id}/preview`, { method: 'POST' }),

  // ---- ozon pricing strategies (внешний репрайсер Ozon) ----
  ozonCompetitors: () => http<{ competitor: OzonCompetitor[]; total: number }>('/api/ozon/competitors?limit=50'),
  ozonStrategies: () => http<{ strategies: OzonStrategy[]; total: number }>('/api/ozon/pricing-strategies?limit=49'),
  ozonStrategyInfo: (id: string) => http<OzonStrategyInfo>(`/api/ozon/pricing-strategies/${id}`),
  ozonStrategyCreate: (body: OzonStrategyCreatePayload) => http<{ strategyId: string }>('/api/ozon/pricing-strategies', { method: 'POST', body: JSON.stringify(body) }),
  ozonStrategyUpdate: (id: string, body: OzonStrategyCreatePayload) => http<{ ok: boolean }>(`/api/ozon/pricing-strategies/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  ozonStrategyDelete: (id: string) => http<void>(`/api/ozon/pricing-strategies/${id}`, { method: 'DELETE' }),
  ozonStrategyToggle: (id: string, enabled: boolean) => http<{ ok: boolean }>(`/api/ozon/pricing-strategies/${id}/toggle`, { method: 'POST', body: JSON.stringify({ enabled }) }),
  ozonStrategyProducts: (id: string) => http<{ products: OzonStrategyProduct[]; total: number }>(`/api/ozon/pricing-strategies/${id}/products?limit=100`),
  ozonStrategyProductsAdd: (id: string, productIds: string[]) => http<{ errors: { code: string; error: string; product_id: number }[]; failed_product_count: number }>(`/api/ozon/pricing-strategies/${id}/products/add`, { method: 'POST', body: JSON.stringify({ product_ids: productIds }) }),
  ozonStrategyProductsDelete: (id: string, productIds: string[]) => http<{ ok: boolean }>(`/api/ozon/pricing-strategies/${id}/products/delete`, { method: 'POST', body: JSON.stringify({ product_ids: productIds }) }),

  // ---- RRP (protection) ----
  rrpSync: () => http<{ accepted: boolean; jobId: string }>('/api/rrp/sync', { method: 'POST' }),
  rrpEnforce: (dryRun = false) => http<{ accepted: boolean; jobId: string; dryRun: boolean }>('/api/rrp/enforce', { method: 'POST', body: JSON.stringify({ dryRun }) }),
  rrpPreview: () => http<RrpPreview>('/api/rrp/preview', { method: 'POST' }),

  // ---- sync ----
  syncRuns: (kind?: string) => http<{ items: { id: string; kind: string; status: string; startedAt: string; finishedAt: string | null; itemsProcessed: number; errorText: string | null }[] }>(`/api/sync/runs${kind ? `?kind=${kind}` : ''}`),
  triggerSync: (kind: 'products' | 'prices' | 'stocks' | 'rrp') =>
    http<{ accepted: boolean; kind: string; note?: string; jobId?: string }>(`/api/sync/${kind}/trigger`, { method: 'POST' }),
};

// ============ strategy types (mirror packages/shared) ============

export type MetricKey =
  | 'stock_total' | 'turnover_days' | 'ext_price_index' | 'ozon_price_index' | 'color_index'
  | 'ordered_units_7d' | 'revenue_7d' | 'hits_view_7d' | 'conv_tocart_7d' | 'position_category' | 'buyout_rate_30d';

export type CompareOp = 'lt' | 'lte' | 'gt' | 'gte' | 'eq' | 'neq';

export type Action =
  | { kind: 'set_price'; value: number }
  | { kind: 'increase_pct'; value: number }
  | { kind: 'decrease_pct'; value: number }
  | { kind: 'increase_abs'; value: number }
  | { kind: 'decrease_abs'; value: number }
  | { kind: 'match_ext_min'; offsetPct: number };

export interface Rule {
  name: string;
  when: { metric: MetricKey; op: CompareOp; value: number | string }[];
  then: Action;
}

export interface StrategyConfig {
  globalMinPrice?: number;
  globalMaxPrice?: number;
  cooldownMin: number;
  dryRun: boolean;
  enforceBreakeven: boolean;
  minMargin: number;
  rules: Rule[];
}

export interface StrategyCreatePayload {
  name: string;
  scheduleCron?: string;
  enabled?: boolean;
  config: StrategyConfig;
}

export interface StrategyDetail {
  id: number;
  name: string;
  enabled: boolean;
  scheduleCron: string;
  config: StrategyConfig;
  products: {
    strategyId: number;
    productId: string;
    minPrice: string | null;
    maxPrice: string | null;
    product: { productId: string; offerId: string; name: string };
  }[];
}

export interface PriceJob {
  id: string;
  strategyId: number;
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
  productsTried: number;
  productsUpdated: number;
  productsSkipped: number;
  errorText: string | null;
}

export interface RrpPreviewDecision {
  productId: string;
  offerId: string;
  rrp: number;
  effective: number;
  ourPrice: number;
  newPrice?: number;
  reason: string;
}

export interface RrpPreview {
  jobId: string;
  productsChecked: number;
  productsAdjusted: number;
  productsSkipped: number;
  decisions: RrpPreviewDecision[];
}

// ============ Ozon Pricing Strategy types ============

export interface OzonCompetitor {
  id: number;
  name: string;
}

export interface OzonStrategy {
  id: string;
  name: string;
  type: 'MIN_EXT_PRICE' | 'COMP_PRICE';
  update_type?: string;
  enabled: boolean;
  products_count: number;
  competitors_count?: number;
  updated_at?: string;
}

export interface OzonStrategyInfo {
  strategy_id: string;
  strategy_name: string;
  strategy_descr?: string;
  type: 'MIN_EXT_PRICE' | 'COMP_PRICE';
  enabled: boolean;
  competitors: { competitor_id: number; coefficient: number }[];
}

export interface OzonStrategyCreatePayload {
  strategy_name: string;
  strategy_descr?: string;
  enabled: boolean;
  competitors: { competitor_id: number; coefficient: number }[];
}

export interface OzonStrategyProduct {
  product_id: number;
  is_enabled: boolean;
  price?: string;
  price_downloaded?: boolean;
  strategy_id?: string;
  strategy_product_price?: string;
  product_min_price?: string;
}

export interface PreviewDecision {
  productId: string;
  offerId: string;
  decision:
    | { kind: 'no_change'; reason: string }
    | { kind: 'change'; newPrice: number; rule: string; rawTarget: number; reason: string }
    | { kind: 'skip_no_price'; reason: string }
    | { kind: 'skip_below_min'; rawTarget: number; floor: number; reason: string }
    | { kind: 'skip_above_max'; rawTarget: number; ceiling: number; reason: string };
}
